import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createBlock as _createBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "my-3" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { key: 1 }
const _hoisted_4 = { key: 1 }
const _hoisted_5 = ["id"]
const _hoisted_6 = { key: 4 }
const _hoisted_7 = ["innerHTML"]
const _hoisted_8 = { key: 1 }

import { computed, ref, watch } from 'vue';
import { useQuery } from '@vue/apollo-composable';
import { LogsDocument, LogType } from '@/gql/graphql';
import { truncateText } from '@/utils/string-utils';
import {
  BAlert,
  BBadge,
  BButton,
  BButtonGroup,
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BModal,
  BPagination,
  BSpinner,
  BTable,
  BTooltip,
} from 'bootstrap-vue-next';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { formatDateTime, formatDateTimeStringToIso } from '@/utils/date-utils';
import { highlightJson } from '@/utils/prism-utils';
import { PAGING_ITEMS_PER_PAGE, TRUNCATE_TEXT_LENGTH } from '@/constants';

// Filtering and paging state

export default /*@__PURE__*/_defineComponent({
  __name: 'AdminLogsView',
  setup(__props) {

const organizationId = ref<number | null>(null);
const userId = ref<number | null>(null);
const actionPattern = ref('');
const textPattern = ref('');
const levelPattern = ref('');
const dateFrom = ref<string | null>(null);
const timeFrom = ref<string | null>(null);
const dateTo = ref<string | null>(null);
const timeTo = ref<string | null>(null);
const dateTimeFrom = ref<string | null>(null);
const dateTimeTo = ref<string | null>(null);
const pagingPageCurrent = ref(1);
const pagingItemsPerPage = ref(PAGING_ITEMS_PER_PAGE);
const pagingTotalRows = computed(() => {
  return logsQueryResult.value?.logs?.total || 0;
});

// Log detail state
const showModalLogDetail = ref(false);
const selectedLog = ref<LogType | null>(null);

// --- Query Logs ---
const theLogs = computed(() => {
  const logList = logsQueryResult.value?.logs?.logs || [];
  return logList
    .filter((log1) => log1 && log1 !== null)
    .map((aLog) => ({
      ...aLog,
      createdAt: aLog?.createdAt ? new Date(aLog.createdAt) : undefined,
      _rowVariant: logLevelVariant(aLog?.level),
    }));
});

const showLogQueryError = computed(() => {
  return logsQueryError.value !== null;
});

const {
  result: logsQueryResult,
  loading: logsQueryInProgress,
  error: logsQueryError,
  refetch: refetchLogs,
} = useQuery(
  LogsDocument,
  {
    organizationId: organizationId.value,
    userId: userId.value,
    actionPattern: actionPattern.value,
    textPattern: textPattern.value,
    levelPattern: levelPattern.value,
    before: dateTimeFrom.value,
    after: dateTimeTo.value,
    pagingPageCurrent: pagingPageCurrent.value,
    pagingItemsPerPage: pagingItemsPerPage.value,
  },
  {
    fetchPolicy: 'no-cache',
  }
);

function clearFilter() {
  organizationId.value = null;
  userId.value = null;
  actionPattern.value = '';
  textPattern.value = '';
  levelPattern.value = '';
  dateFrom.value = null;
  timeFrom.value = null;
  dateTo.value = null;
  timeTo.value = null;
  dateTimeFrom.value = null;
  dateTimeTo.value = null;
  pagingPageCurrent.value = 1;

  requestRefetchLogs();
}

function requestRefetchLogs() {
  // check integer values are integer values
  if (!Number.isInteger(organizationId.value)) {
    console.warn('Invalid organization ID. Resetting to null.');
    organizationId.value = null;
  }
  if (!Number.isInteger(userId.value)) {
    console.warn('Invalid user ID. Resetting to null.');
    userId.value = null;
  }

  // convert date and time to dateTime object (current timezone)
  dateTimeFrom.value = formatDateTimeStringToIso(
    dateFrom.value,
    timeFrom.value
  );
  dateTimeTo.value = formatDateTimeStringToIso(dateTo.value, timeTo.value);

  // refetch the query
  logsQueryInProgress.value = true;
  logsQueryError.value = null;

  refetchLogs({
    organizationId: organizationId.value,
    userId: userId.value,
    actionPattern: actionPattern.value,
    textPattern: textPattern.value,
    levelPattern: levelPattern.value,
    before: dateTimeTo.value,
    after: dateTimeFrom.value,
    pagingPageCurrent: pagingPageCurrent.value,
    pagingItemsPerPage: pagingItemsPerPage.value,
  })
    ?.catch((err) => {
      logsQueryInProgress.value = false;
      logsQueryError.value = err;
      console.error('Refetch failed:', err);
    })
    .then(() => {
      logsQueryInProgress.value = false;
      console.log('refetched');
    });
}

const fields = [
  {
    key: 'createdAt',
    label: 'Created',
    sortable: false,
    formatter: formatDateTime,
    tdClass: 'align-middle',
  },
  {
    key: 'organization.name',
    label: 'Organization',
    sortable: false,
    tdClass: 'align-middle',
  },
  {
    key: 'user.email',
    label: 'User',
    sortable: false,
    tdClass: 'align-middle',
  },
  {
    key: 'action',
    label: 'Action',
    sortable: false,
    tdClass: 'align-middle',
  },
  {
    key: 'level',
    label: 'Level',
    sortable: false,
    tdClass: 'align-middle',
  },
  {
    key: 'text',
    label: 'Text summary',
    sortable: false,
    tdClass: 'align-middle',
  },
  {
    key: 'actions',
    label: '',
    tdClass: 'align-middle text-end min-width',
  },
];

function onLogDetail(theLog: LogType) {
  showModalLogDetail.value = true;
  selectedLog.value = theLog;
}

function onLogDetailClose() {
  showModalLogDetail.value = false;
  selectedLog.value = null;
}

function onSearchRequested() {
  if (pagingPageCurrent.value !== 1) {
    pagingPageCurrent.value = 1;
  } else {
    requestRefetchLogs();
  }
}

function logLevelVariant(level: string | null | undefined): string {
  if (!level) {
    return 'secondary';
  }

  switch (level) {
    case 'debug':
      return 'info';
    case 'info':
      return 'primary';
    case 'warning':
      return 'warning';
    case 'error':
      return 'danger';
    case 'critical':
      return 'danger';
    default:
      return 'secondary';
  }
}

watch(pagingPageCurrent, (newPage) => {
  console.debug('page changed to', newPage);
  requestRefetchLogs();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("h1", null, [
      _createVNode(_unref(FontAwesomeIcon), { icon: ['fas', 'file-lines'] }),
      _cache[13] || (_cache[13] = _createTextVNode(" Audit logs", -1))
    ]),
    _cache[26] || (_cache[26] = _createElementVNode("hr", null, null, -1)),
    _cache[27] || (_cache[27] = _createElementVNode("p", null, " Audit logs provide a centralized, real-time view of all critical events and system changes across the application. This page enables administrators to browse, filter, and analyze audit entries for debugging, security investigations, and technical support, ensuring that any suspicious activity, errors, or compliance issues are promptly identified and addressed. ", -1)),
    _createElementVNode("div", null, [
      _createVNode(_unref(BAlert), {
        modelValue: showLogQueryError.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((showLogQueryError).value = $event)),
        variant: "danger",
        dismissible: "",
        class: "mt-3"
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(logsQueryError)?.message) + " ", 1),
          _createVNode(_unref(BButton), {
            onClick: requestRefetchLogs,
            variant: "outline-dark",
            class: "btn-sm"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(FontAwesomeIcon), { icon: ['fas', 'sync'] }),
              _cache[14] || (_cache[14] = _createTextVNode(" Retry", -1))
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_unref(BForm), {
          class: "d-flex flex-row align-items-end flex-wrap gap-2",
          onSubmit: _withModifiers(onSearchRequested, ["prevent"])
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(BFormGroup), {
              label: "Organization ID",
              "label-for": "organization-id"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "organization-id",
                  type: "number",
                  modelValue: organizationId.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((organizationId).value = $event)),
                  modelModifiers: { number: true },
                  placeholder: "Organization ID"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "User ID",
              "label-for": "user-id"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "user-id",
                  type: "number",
                  modelValue: userId.value,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((userId).value = $event)),
                  modelModifiers: { number: true },
                  placeholder: "User ID"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Action filter",
              "label-for": "search-action"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "search-action",
                  type: "text",
                  modelValue: actionPattern.value,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((actionPattern).value = $event)),
                  placeholder: "e.g. project delete"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Level filter",
              "label-for": "search-level"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "search-level",
                  type: "text",
                  modelValue: levelPattern.value,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((levelPattern).value = $event)),
                  placeholder: "e.g. warning"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Search text",
              "label-for": "search-text"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "search-text",
                  type: "text",
                  modelValue: textPattern.value,
                  "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((textPattern).value = $event)),
                  placeholder: "e.g. user % details updated"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Date from",
              "label-for": "date-from"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "date-from",
                  type: "date",
                  modelValue: dateFrom.value,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((dateFrom).value = $event))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Time from",
              "label-for": "time-from"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "time-from",
                  type: "time",
                  modelValue: timeFrom.value,
                  "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((timeFrom).value = $event))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Date to",
              "label-for": "date-to"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "date-to",
                  type: "date",
                  modelValue: dateTo.value,
                  "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((dateTo).value = $event))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Time to",
              "label-for": "time-to"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "time-to",
                  type: "time",
                  modelValue: timeTo.value,
                  "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((timeTo).value = $event))
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BButton), {
              variant: "outline-secondary",
              onClick: clearFilter
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(FontAwesomeIcon), {
                  icon: "trash",
                  class: "me-1"
                }),
                _cache[15] || (_cache[15] = _createTextVNode(" Clear filter ", -1))
              ]),
              _: 1
            }),
            _createVNode(_unref(BButton), {
              variant: "outline-dark",
              type: "submit",
              disabled: _unref(logsQueryInProgress)
            }, {
              default: _withCtx(() => [
                (_unref(logsQueryInProgress))
                  ? (_openBlock(), _createElementBlock("span", _hoisted_2, [
                      _createVNode(_unref(BSpinner), {
                        small: "",
                        label: ""
                      }),
                      _cache[16] || (_cache[16] = _createTextVNode(" Searching ", -1))
                    ]))
                  : (_openBlock(), _createElementBlock("span", _hoisted_3, [
                      _createVNode(_unref(FontAwesomeIcon), {
                        icon: "magnifying-glass",
                        class: "me-1"
                      }),
                      _cache[17] || (_cache[17] = _createTextVNode(" Search ", -1))
                    ]))
              ]),
              _: 1
            }, 8, ["disabled"])
          ]),
          _: 1
        })
      ]),
      (pagingTotalRows.value > pagingItemsPerPage.value)
        ? (_openBlock(), _createBlock(_unref(BPagination), {
            key: 0,
            modelValue: pagingPageCurrent.value,
            "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((pagingPageCurrent).value = $event)),
            "total-rows": pagingTotalRows.value,
            "per-page": pagingItemsPerPage.value,
            pills: "",
            size: "sm"
          }, null, 8, ["modelValue", "total-rows", "per-page"]))
        : _createCommentVNode("", true),
      (!theLogs.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [...(_cache[18] || (_cache[18] = [
            _createElementVNode("h5", { class: "my-5 py-5 text-center" }, " There are no logs on this page correspoding to the filter. ", -1)
          ]))]))
        : (_openBlock(), _createBlock(_unref(BCard), {
            key: 2,
            class: "my-3"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(BTable), {
                striped: "",
                responsive: "",
                hover: "",
                items: theLogs.value,
                fields: fields,
                busy: _unref(logsQueryInProgress),
                id: "logs-table",
                class: "browser-table",
                onRowClicked: onLogDetail
              }, {
                "cell(level)": _withCtx((data) => [
                  _createVNode(_unref(BBadge), {
                    variant: logLevelVariant(data.value)
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(data.value), 1)
                    ]),
                    _: 2
                  }, 1032, ["variant"])
                ]),
                "cell(text)": _withCtx((data) => [
                  _createElementVNode("span", {
                    id: 'log-text-' + data.item.id
                  }, _toDisplayString(_unref(truncateText)(data.value, _unref(TRUNCATE_TEXT_LENGTH))), 9, _hoisted_5),
                  (data.value && data.value.length > _unref(TRUNCATE_TEXT_LENGTH))
                    ? (_openBlock(), _createBlock(_unref(BTooltip), {
                        key: 0,
                        target: 'log-text-' + data.item.id,
                        class: "large-tooltip"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(data.value), 1)
                        ]),
                        _: 2
                      }, 1032, ["target"]))
                    : _createCommentVNode("", true)
                ]),
                "cell(actions)": _withCtx((data) => [
                  _createVNode(_unref(BButtonGroup), null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(BButton), {
                        variant: "outline-dark",
                        class: "btn-sm",
                        onClick: ($event: any) => (onLogDetail(data.item))
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(FontAwesomeIcon), { icon: ['fa', 'magnifying-glass'] })
                        ]),
                        _: 1
                      }, 8, ["onClick"])
                    ]),
                    _: 2
                  }, 1024)
                ]),
                _: 1
              }, 8, ["items", "busy"])
            ]),
            _: 1
          })),
      (pagingTotalRows.value > pagingItemsPerPage.value)
        ? (_openBlock(), _createBlock(_unref(BPagination), {
            key: 3,
            modelValue: pagingPageCurrent.value,
            "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((pagingPageCurrent).value = $event)),
            "total-rows": pagingTotalRows.value,
            "per-page": pagingItemsPerPage.value,
            pills: "",
            size: "sm"
          }, null, 8, ["modelValue", "total-rows", "per-page"]))
        : _createCommentVNode("", true),
      (selectedLog.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            _createVNode(_unref(BModal), {
              modelValue: showModalLogDetail.value,
              "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((showModalLogDetail).value = $event)),
              onClose: onLogDetailClose,
              title: "Log Detail",
              "ok-only": "",
              "ok-title": "Close",
              size: "",
              class: "modal-lg"
            }, {
              title: _withCtx(() => [
                _createVNode(_unref(BBadge), {
                  variant: logLevelVariant(selectedLog.value.level)
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(selectedLog.value.level), 1)
                  ]),
                  _: 1
                }, 8, ["variant"]),
                _createTextVNode(" " + _toDisplayString(selectedLog.value.action), 1)
              ]),
              default: _withCtx(() => [
                _createElementVNode("p", null, [
                  _cache[23] || (_cache[23] = _createElementVNode("strong", null, "Created:", -1)),
                  _createTextVNode(" " + _toDisplayString(_unref(formatDateTime)(selectedLog.value.createdAt)) + " ", 1),
                  (selectedLog.value.user)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _cache[19] || (_cache[19] = _createElementVNode("br", null, null, -1)),
                        _cache[20] || (_cache[20] = _createElementVNode("strong", null, "User:", -1)),
                        _createTextVNode(" " + _toDisplayString(selectedLog.value.user.givenName) + " " + _toDisplayString(selectedLog.value.user.familyName) + " (" + _toDisplayString(selectedLog.value.user.email) + ") ", 1)
                      ], 64))
                    : _createCommentVNode("", true),
                  (selectedLog.value.organization)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _cache[21] || (_cache[21] = _createElementVNode("br", null, null, -1)),
                        _cache[22] || (_cache[22] = _createElementVNode("strong", null, "Organization:", -1)),
                        _createTextVNode(" " + _toDisplayString(selectedLog.value.organization.name), 1)
                      ], 64))
                    : _createCommentVNode("", true)
                ]),
                (selectedLog.value.details)
                  ? (_openBlock(), _createElementBlock("pre", {
                      key: 0,
                      class: "code-highlight",
                      innerHTML: _unref(highlightJson)(selectedLog.value.details)
                    }, null, 8, _hoisted_7))
                  : _createCommentVNode("", true),
                (selectedLog.value.text)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_8, [
                      _cache[24] || (_cache[24] = _createElementVNode("strong", null, "Text summary", -1)),
                      _cache[25] || (_cache[25] = _createElementVNode("br", null, null, -1)),
                      _createTextVNode(" " + _toDisplayString(selectedLog.value.text), 1)
                    ]))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }, 8, ["modelValue"])
          ]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})